import ControlledRadioGroupField from '../controlledFields/ControlledRadioGroupField';
import { FieldEnum, FieldMap } from './sectionSchema';
import ControlledDateField from '../controlledFields/ControlledDateField';
import invariant from 'tiny-invariant';
import type { ComponentProps } from 'react';
import type { RadioGroupFieldConfig, SingleSelectFieldConfig } from './types';
import ControlledSingleSelectField from '../controlledFields/ControlledSingleSelectField';
import ControlledTextField from '../controlledFields/ControlledTextFieldNew';
import ControlledNumberField from '../controlledFields/ControlledNumberField';

interface BaseFieldProps {
  name: FieldEnum;
  disabled?: boolean;
}

/** The plan is to use these SectionFields to dynamically render fields based on the FieldMap configuration */
export const SectionTextField = ({ name, disabled }: BaseFieldProps) => {
  const { dataId, helperText, label, required, type } = FieldMap[name];
  invariant(
    type === 'text',
    `type for ${name} must be set to text to use the TextField`,
  );
  return (
    <ControlledTextField
      dataId={dataId}
      disabled={disabled}
      helperText={helperText}
      name={name}
      label={label}
      required={required}
      type={type}
    />
  );
};

export const SectionDateField = ({ disabled, name }: BaseFieldProps) => {
  const { dataId, helperText, label, required, type } = FieldMap[name];
  invariant(
    type === 'date',
    `Field type for ${name} must be set to date to use the DateField`,
  );
  return (
    <ControlledDateField
      dataId={dataId}
      disabled={disabled}
      helperText={helperText}
      name={name}
      label={label}
      required={required}
    />
  );
};

export const SectionTextAreaField = ({ disabled, name }: BaseFieldProps) => {
  const { dataId, helperText, label, required, placeholder, type } =
    FieldMap[name];

  return (
    <ControlledTextField
      dataId={dataId}
      disabled={disabled}
      name={name}
      label={label}
      required={required}
      type={type}
      helperText={helperText}
      placeholder={placeholder}
      multiline
      rows={5}
    />
  );
};

/** WIP
 * TODO: CA - Replace with a Number input that has capability to display decimals, has a number prefix and can accept a ref */
export const SectionNumberField = ({ disabled, name }: BaseFieldProps) => {
  const { dataId, helperText, label, required, type } = FieldMap[name];
  return (
    <ControlledNumberField
      dataId={dataId}
      disabled={disabled}
      name={name}
      label={label}
      required={required}
      type={type}
      helperText={helperText}
    />
  );
};

export const SectionSingleSelectField = ({
  disabled,
  name,
  options,
  ...props
}: BaseFieldProps & {
  placeholder?: ComponentProps<
    typeof ControlledSingleSelectField
  >['placeholder'];
  options: ComponentProps<typeof ControlledSingleSelectField>['options'];
}) => {
  const { dataId, helperText, label, required, type } = FieldMap[
    name
  ] as SingleSelectFieldConfig;
  invariant(
    type === 'single-select',
    `Field type for ${name} must be set to single-select to use the SingleSelectField`,
  );

  return (
    <ControlledSingleSelectField
      {...props}
      dataId={dataId}
      disabled={disabled}
      helperText={helperText}
      name={name}
      label={label}
      required={required}
      options={options}
    />
  );
};

export const SectionRadioGroupField = ({ disabled, name }: BaseFieldProps) => {
  const { dataId, helperText, label, required, type, options } = FieldMap[
    name
  ] as RadioGroupFieldConfig;

  invariant(options, `Options are not provided for radio field:  ${name}`);
  invariant(
    type === 'radio-group',
    `Field type for ${name} must be set to radio to use the RadioGroupField`,
  );
  return (
    <ControlledRadioGroupField
      dataId={dataId}
      disabled={disabled}
      helperText={helperText}
      label={label}
      name={name}
      options={options}
      required={required}
    />
  );
};
